import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Autoplay from 'embla-carousel-autoplay';
import { Carousel } from '@heroui-pro/react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  Drawer,
  Input,
  Label,
  Spinner,
  Tabs,
  toast,
  useOverlayState,
} from '@heroui/react';
import { formatDateTime } from '@his/shared/utils/formatTime';
import { useWeChatShare } from '@his/shared';
import { LotteryRulesTimeline } from '@/components/activities/LotteryRulesTimeline';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { Gender } from '@/pages/activities/items/olympic/types';
import { WechatAction, type WechatActionType } from '@/components/common/wechat-action';
import { WechatActionTips, type WechatActionTipsType } from '@/components/common/wechat-action-tips';

import fcbCover from '@/assets/images/fcb-cover.webp';
import bayernLogo from '../../../../assets/images/FC-Bayern-Logo.svg';

const PAGE_TITLE_FALLBACK = '共赴拜仁之战';

const INVITER_REQUIRED_MSG = '缺少邀请人，无法参加活动，请联系您身边的安联寿险规划师。';

/** localStorage：本设备是否已完成拜仁预测登记（按活动维度） */
function fcbRegistrationStorageKey(activityHashId: string) {
  return `fcb_match_prediction_registered_${activityHashId}`;
}

/** 与冬奥公开页一致：中国大陆 11 位手机号 */
const CN_MOBILE_REGEX = /^1[3-9]\d{9}$/;

function isValidCnMobile(value: string): boolean {
  return CN_MOBILE_REGEX.test(value.trim());
}

/** 格式化为 YYYY-MM-DD HH:MM（与 olympic 抽奖规则分组一致） */
function formatDateTimeMinute(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

/** 姓名列展示：取 name 首字 + 性别（先生/女士），与 olympic 获奖名单一致 */
function nameWithGenderDisplay(name: string | undefined | null, gender: string | undefined | null): string {
  const first = (name ?? '').trim()[0];
  if (!first) return '—';
  const suffix = gender === 'MALE' ? '先生' : gender === 'FEMALE' ? '女士' : '';
  return first + suffix;
}

function genderToApi(g: Gender): 'MALE' | 'FEMALE' {
  return g === Gender.MALE ? 'MALE' : 'FEMALE';
}

type MatchPredictionChoice = 'bayern' | 'psg';

function predictionToExtraValue(choice: MatchPredictionChoice): 'BAYERN_WIN' | 'PSG_WIN' {
  if (choice === 'bayern') return 'BAYERN_WIN';
  return 'PSG_WIN';
}

/** 与预测三按钮文案一致 */
function matchPredictionButtonLabel(choice: MatchPredictionChoice | null): string {
  if (!choice) return '—';
  if (choice === 'bayern') return '拜仁晋级';
  return '巴黎晋级';
}

/**
 * 拜仁主题活动页（公开访问）
 * URL 与 olympic 一致：`?activity=<活动hashid>&inviter=<邀请人hashid>`（inviter 供后续业务扩展）
 */
export default function FcbBayernActivityPage() {
  const [searchParams] = useSearchParams();
  const activityId = searchParams.get('activity') ?? '';
  const inviterId = searchParams.get('inviter') ?? '';

  const [tab, setTab] = useState('prediction');

  const { data: activity, isLoading: activityLoading } = trpc.activities.get.useQuery(
    { id: activityId },
    { enabled: !!activityId }
  );

  const { data: prizes, isLoading: prizesLoading } = trpc.activityPrizes.listAll.useQuery(
    { activityId: activityId || '' },
    { enabled: !!activityId }
  );
  const { data: lotteryRules, isLoading: rulesLoading } = trpc.activityLotteryRules.listAll.useQuery(
    {
      activityId: activityId || '',
      sortBy: 'lotteryTime',
      sortOrder: 'asc',
    },
    { enabled: !!activityId }
  );

  const prizesAutoplay = useRef(Autoplay({ delay: 3000, stopOnInteraction: true }));

  const [winnersPage, setWinnersPage] = useState(1);
  const winnersPageSize = 30;
  const [winnersKeyword, setWinnersKeyword] = useState('');
  const [winnersKeywordInput, setWinnersKeywordInput] = useState('');

  const winnersQueryInput = useMemo(
    () => ({
      activityId: activityId || '',
      isWinner: true as const,
      keyword: winnersKeyword.trim() || undefined,
      page: winnersPage,
      pageSize: winnersPageSize,
      sortBy: 'createdAt' as const,
      sortOrder: 'desc' as const,
      /** 与后端 DTO 一致；tRPC 生成类型将 where 标为必填时的兼容写法 */
      where: {},
    }),
    [activityId, winnersKeyword, winnersPage]
  );

  const { data: winnersData, isLoading: winnersLoading } = trpc.activityRegistrations.list.useQuery(
    winnersQueryInput,
    { enabled: !!activityId }
  );

  const winnersItems = winnersData?.items ?? [];
  const winnersTotalPages = winnersData?.totalPages ?? 1;
  const winnersHasPrev = winnersData?.hasPrev ?? false;
  const winnersHasNext = winnersData?.hasNext ?? false;

  const winnersKeywordTrimmed = winnersKeyword.trim();
  const winnersLookupInput = useMemo(
    () =>
      winnersKeywordTrimmed
        ? {
            activityId: activityId || '',
            keyword: winnersKeywordTrimmed,
            pageSize: 1,
            sortBy: 'createdAt' as const,
            sortOrder: 'desc' as const,
            isWinner: undefined,
            where: {},
          }
        : null,
    [activityId, winnersKeywordTrimmed]
  );

  const { data: winnersLookupData, isLoading: winnersLookupLoading } =
    trpc.activityRegistrations.list.useQuery(
      winnersLookupInput ?? { activityId: '', pageSize: 1, isWinner: undefined, where: {} },
      {
        enabled: !!activityId && !!winnersLookupInput,
      }
    );

  type SearchResultState = 'loading' | 'not_registered' | 'not_winner' | 'winner' | null;
  const winnersSearchResultState: SearchResultState = useMemo(() => {
    if (!winnersKeywordTrimmed) return null;
    if (winnersLookupLoading) return 'loading';
    if (!winnersLookupData?.items?.length) return 'not_registered';
    const item = winnersLookupData.items[0] as { isWinner?: boolean };
    if (item.isWinner === true) return 'winner';
    return 'not_winner';
  }, [winnersKeywordTrimmed, winnersLookupLoading, winnersLookupData?.items]);

  const handleWinnersSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = winnersKeywordInput.trim();
    if (trimmed === '') {
      setWinnersKeyword('');
      setWinnersPage(1);
      return;
    }
    if (!isValidCnMobile(trimmed)) {
      toast.error('请输入正确的 11 位中国手机号');
      return;
    }
    setWinnersKeyword(trimmed);
    setWinnersPage(1);
  };

  const groupedRules = useMemo(() => {
    if (!lotteryRules || !prizes) return [];
    const prizeMap = new Map(prizes.map((p) => [p.id, p]));
    const groups = new Map<string, typeof lotteryRules>();
    lotteryRules.forEach((rule) => {
      if (!rule.lotteryTime) return;
      const timeKey = formatDateTimeMinute(rule.lotteryTime);
      if (!groups.has(timeKey)) groups.set(timeKey, []);
      groups.get(timeKey)!.push(rule);
    });
    return Array.from(groups.entries())
      .map(([, rules]) => {
        const first = rules[0];
        const displayTime = first?.lotteryTime ? formatDateTimeMinute(first.lotteryTime) : '';
        const mappedRules = rules.map((rule) => ({
          ...rule,
          prize: rule.prizeId ? prizeMap.get(rule.prizeId) : null,
        }));
        const allCompleted = mappedRules.every((r) => r.isCompleted === true);
        return {
          time: displayTime,
          rules: mappedRules,
          allCompleted,
        };
      })
      .sort((a, b) => {
        const timeA = a.rules[0]?.lotteryTime ? new Date(a.rules[0].lotteryTime).getTime() : 0;
        const timeB = b.rules[0]?.lotteryTime ? new Date(b.rules[0].lotteryTime).getTime() : 0;
        return timeA - timeB;
      });
  }, [lotteryRules, prizes]);

  const user = useAppSelector((state) => state.auth.user);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const now = Date.now();
  const startTimeMs = activity?.startTime ? new Date(activity.startTime).getTime() : null;
  const endTimeMs = activity?.endTime ? new Date(activity.endTime).getTime() : null;
  const notStarted = startTimeMs != null && now < startTimeMs;
  const ended = endTimeMs != null && now > endTimeMs;
  const submitDisabledByTime = notStarted || ended;

  const [pendingPrediction, setPendingPrediction] = useState<MatchPredictionChoice | null>(null);
  const registerDrawer = useOverlayState({
    defaultOpen: false,
    onOpenChange: (open) => {
      if (!open) {
        setPendingPrediction(null);
      }
    },
  });
  const [regName, setRegName] = useState('');
  const [regGender, setRegGender] = useState<Gender | null>(null);
  const [regPhone, setRegPhone] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [hasLocalRegistration, setHasLocalRegistration] = useState(false);
  const [wechatTipAction, setWechatTipAction] = useState<WechatActionTipsType | null>(null);

  useEffect(() => {
    if (!activityId || typeof window === 'undefined') {
      setHasLocalRegistration(false);
      return;
    }
    try {
      const v = window.localStorage.getItem(fcbRegistrationStorageKey(activityId));
      setHasLocalRegistration(v === '1');
    } catch {
      setHasLocalRegistration(false);
    }
  }, [activityId]);

  const createByInvitation = trpc.activityRegistrations.createByInvitation.useMutation();

  useEffect(() => {
    if (activity?.title) {
      document.title = activity.title;
    } else {
      document.title = PAGE_TITLE_FALLBACK;
    }
  }, [activity?.title]);

  const { ready: wxReady, init: wxInit, setShareInfo } = useWeChatShare();

  useEffect(() => {
    wxInit();
  }, [wxInit]);

  useEffect(() => {
    if (!wxReady || !activity) return;
    const plainDesc = activity.description
      ? activity.description.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().slice(0, 60)
      : '';
    /** 与页顶封面一致：优先活动 coverImage；与冬奥页一致再回退用户头像（`setShareInfo` 同时配置转发好友 + 朋友圈） */
    const shareImg = activity.coverImage?.trim() || user?.avatarUrl || '';
    setShareInfo({
      title: activity.title ?? PAGE_TITLE_FALLBACK,
      desc: plainDesc,
      link: window.location.href,
      imgUrl: shareImg,
    });
  }, [wxReady, activity, user?.avatarUrl, setShareInfo]);

  const openPredictionRegister = (choice: MatchPredictionChoice) => {
    if (hasLocalRegistration) {
      toast.error('您已登记过，无需重复提交');
      return;
    }
    if (isAuthenticated && user?.id && inviterId && user.id === inviterId) {
      toast.error('不支持邀请者填写表单');
      return;
    }
    if (!activityId) {
      toast.error('缺少活动信息，请通过正确的链接参与。');
      return;
    }
    if (!inviterId) {
      toast.error(INVITER_REQUIRED_MSG);
      return;
    }
    if (activityLoading) {
      toast.error('活动信息加载中，请稍候再试');
      return;
    }
    if (submitDisabledByTime) {
      toast.error(notStarted ? '活动尚未开始' : '活动已结束');
      return;
    }
    setPendingPrediction(choice);
    registerDrawer.open();
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingPrediction) return;
    if (isAuthenticated && user?.id && inviterId && user.id === inviterId) {
      toast.error('不支持邀请者填写表单');
      return;
    }
    if (!activityId || !inviterId) {
      toast.error(!inviterId ? INVITER_REQUIRED_MSG : '缺少活动信息，请通过正确的链接参与。');
      return;
    }
    if (submitDisabledByTime) {
      toast.error(notStarted ? '活动尚未开始' : '活动已结束');
      return;
    }

    const trimmedName = regName.trim();
    if (!trimmedName || trimmedName.length < 1) {
      toast.error('请输入姓名');
      return;
    }
    if (regGender === null) {
      toast.error('请选择尊称');
      return;
    }
    const trimmedPhone = regPhone.trim();
    if (!trimmedPhone || !isValidCnMobile(trimmedPhone)) {
      toast.error('手机号输入错误');
      return;
    }

    setIsRegistering(true);
    try {
      await createByInvitation.mutateAsync({
        activityId,
        inviterId,
        name: trimmedName,
        gender: genderToApi(regGender),
        mobile: trimmedPhone,
        extra: {
          fcbMatchPrediction: predictionToExtraValue(pendingPrediction),
        },
      });
      try {
        window.localStorage.setItem(fcbRegistrationStorageKey(activityId), '1');
      } catch {
        /* ignore quota */
      }
      setHasLocalRegistration(true);
      toast.success('登记成功，祝您好运！');
      registerDrawer.close();
      setPendingPrediction(null);
      setRegName('');
      setRegGender(null);
      setRegPhone('');
    } finally {
      setIsRegistering(false);
    }
  };

  const heroCoverSrc =
    activity?.coverImage && activity.coverImage.trim() !== '' ? activity.coverImage : fcbCover;

  const predictionButtonsDisabled =
    hasLocalRegistration || activityLoading || submitDisabledByTime;

  const handleWechatAction = (action: WechatActionType) => {
    const tipAction: WechatActionTipsType = action === 'add-star' ? 'star' : action;
    setWechatTipAction(tipAction);
  };

  return (
    <div className="theme-dark-blue min-h-dvh">
      <div className="relative w-full overflow-hidden">
        <img
          src={heroCoverSrc}
          alt={activity?.title ?? ''}
          className="block h-auto w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/40 via-background/20 to-transparent" />
      </div>

      <div className="mx-auto w-full max-w-lg px-4 pb-36 pt-2">
        <Tabs
          variant="secondary"
          selectedKey={tab}
          onSelectionChange={(key) => setTab(String(key))}
        >
          <Tabs.ListContainer>
            <Tabs.List aria-label="活动分区">
              <Tabs.Tab id="prediction">
                <Tabs.Indicator />
                战局预测
              </Tabs.Tab>
              <Tabs.Tab id="lottery">
                <Tabs.Separator />
                <Tabs.Indicator />
                抽奖安排
              </Tabs.Tab>
              <Tabs.Tab id="results">
                <Tabs.Separator />
                <Tabs.Indicator />
                获奖名单
              </Tabs.Tab>
            </Tabs.List>
          </Tabs.ListContainer>

          <Tabs.Panel id="prediction">
            <div className="mt-6 space-y-6">
            <section className="space-y-2 text-center">
              <p className="text-sm text-default-500">2026年5月7日 03:00</p>
              <p className="text-sm">慕尼黑安联球场</p>
              <p className="text-base font-medium">
                拜仁慕尼黑 <span className="mx-1 text-default-500">VS</span> 巴黎圣日耳曼
              </p>
            </section>

            <section className="space-y-3">
              <div className="rounded-xl border border-border/80 bg-content1/60 p-4">
                <h2 className="mb-3 text-center text-xl font-medium text-default-500">您的神预测是</h2>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={predictionButtonsDisabled}
                    className="aspect-square rounded-lg bg-[oklch(0.55_0.2_25)] p-2 text-base font-medium text-white shadow-sm transition hover:bg-[oklch(0.5_0.2_25)] enabled:active:scale-[0.98] disabled:opacity-50"
                    onClick={() => openPredictionRegister('bayern')}
                  >
                    拜仁晋级
                  </button>
                  <button
                    type="button"
                    disabled={predictionButtonsDisabled}
                    className="aspect-square rounded-lg bg-[oklch(0.45_0.1_200)] p-2 text-base font-medium text-white shadow-sm transition hover:bg-[oklch(0.42_0.11_200)] enabled:active:scale-[0.98] disabled:opacity-50"
                    onClick={() => openPredictionRegister('psg')}
                  >
                    巴黎晋级
                  </button>
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <div className="relative pt-3">
                <span className="absolute left-1/2 top-3 z-10 -translate-x-1/2 -translate-y-1/2 rounded-md bg-[oklch(0.45_0.14_300)] px-2 py-0.5 text-xs font-medium text-white">
                  拜仁欧冠战绩
                </span>
                <Card className="border-border/60 bg-content1 shadow-none">
                  <CardContent className="space-y-4 p-4 pt-10">
                    <div className="flex gap-3">
                      <div className="w-14 shrink-0">
                        <img src={bayernLogo} alt="" className="h-auto w-full" />
                      </div>
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="text-base font-semibold">拜仁慕尼黑</p>
                        <p className="text-xs leading-relaxed text-default-500">
                          首回合4:5负于巴黎圣日尔曼队
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                      <div>
                        <span className="font-semibold text-foreground">11</span>
                        <span className="text-default-500"> 胜</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">0</span>
                        <span className="text-default-500"> 平</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">2</span>
                        <span className="text-default-500"> 负</span>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm">
                      <div className="grid grid-cols-[auto_1fr] gap-x-3 items-start">
                        <span className="shrink-0 text-default-500">近期状态</span>
                        <div className="flex min-w-0 flex-wrap justify-end gap-2">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <span
                              key={i}
                              className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-default-100 text-xs text-default-500"
                            >
                              {i === 4 ? '负' : '胜'}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-[auto_1fr] gap-x-3 items-center">
                        <span className="shrink-0 text-default-500">进球 / 失球</span>
                        <span className="min-w-0 text-right font-medium tabular-nums">42 / 19</span>
                      </div>
                      <div className="grid grid-cols-[auto_1fr] gap-x-3 items-start">
                        <span className="shrink-0 text-default-500">关键球员</span>
                        <p className="min-w-0 text-left leading-relaxed">
                          凯恩、穆西亚拉、基米希、帕夫洛维奇、萨内
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-center text-sm font-medium">与巴黎圣日耳曼历史交锋</h3>
              <div className="grid grid-cols-3 gap-2 text-center text-sm text-white">
                <div className="rounded-lg py-4 font-medium bg-[oklch(0.55_0.2_25)]">
                  <div className="text-lg font-semibold">10</div>
                  <div className="text-xs opacity-90">拜仁胜</div>
                </div>
                <div className="rounded-lg py-4 font-medium bg-[oklch(0.4_0.1_270)]">
                  <div className="text-lg font-semibold">0</div>
                  <div className="text-xs opacity-90">平</div>
                </div>
                <div className="rounded-lg py-4 font-medium bg-[oklch(0.45_0.1_200)]">
                  <div className="text-lg font-semibold">7</div>
                  <div className="text-xs opacity-90">巴黎胜</div>
                </div>
              </div>
            </section>
            </div>
          </Tabs.Panel>

          <Tabs.Panel id="lottery">
            <div className="mt-6">
            {!activityId ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center text-sm text-default-500">
                请在链接中携带活动参数 <span className="font-mono text-foreground">activity</span> 后打开本页。
              </div>
            ) : prizesLoading || rulesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner className="h-8 w-8" />
              </div>
            ) : (
              <div className="space-y-6">
                {prizes && prizes.length > 0 && (
                  <div className="w-full bg-background px-0 py-4">
                    <Carousel
                      className="w-full"
                      opts={{ align: 'start', loop: true }}
                      plugins={[prizesAutoplay.current]}
                    >
                      <Carousel.Content>
                        {prizes.map((prize) => (
                          <Carousel.Item key={prize.id} className="basis-full">
                            <div className="flex flex-col items-center space-y-3">
                              <div className="aspect-square max-w-xs w-full overflow-hidden rounded-lg bg-default-100">
                                {prize.imageUrl ? (
                                  <img
                                    src={prize.imageUrl}
                                    alt={prize.name || '奖品图片'}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-default-500">
                                    暂无图片
                                  </div>
                                )}
                              </div>
                              <div className="space-y-1 text-center">
                                <p className="text-base font-semibold">{prize.name || '未命名奖品'}</p>
                                {prize.quantity != null && (
                                  <p className="text-sm text-default-500">{prize.quantity}个</p>
                                )}
                              </div>
                            </div>
                          </Carousel.Item>
                        ))}
                      </Carousel.Content>
                      {prizes.length > 1 ? (
                        <Carousel.Dots
                          className="mt-4 flex justify-center gap-2"
                          renderDot={({ index, isSelected }) => (
                            <span
                              key={index}
                              className={`inline-block h-2 rounded-full transition-all ${
                                isSelected ? 'w-6 bg-primary' : 'w-2 bg-default-400/30'
                              }`}
                              aria-hidden
                            />
                          )}
                        />
                      ) : null}
                    </Carousel>
                  </div>
                )}
                {groupedRules.length > 0 && (
                  <div className="px-0 pb-4">
                    <LotteryRulesTimeline groups={groupedRules} />
                  </div>
                )}
                {!prizesLoading && !rulesLoading && (!prizes || prizes.length === 0) && (!lotteryRules || lotteryRules.length === 0) && (
                  <div className="flex flex-col items-center justify-center p-4 py-12">
                    <p className="text-center text-default-500">暂无抽奖规则</p>
                  </div>
                )}
              </div>
            )}
            </div>
          </Tabs.Panel>

          <Tabs.Panel id="results">
            <div className="mt-6">
            {!activityId ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center text-sm text-default-500">
                请在链接中携带活动参数 <span className="font-mono text-foreground">activity</span> 后打开本页。
              </div>
            ) : (
              <div className="space-y-4">
                <form onSubmit={handleWinnersSearchSubmit} className="flex gap-2">
                  <Input
                    placeholder="搜索手机号（11 位）"
                    value={winnersKeywordInput}
                    onChange={(e) => setWinnersKeywordInput(e.target.value)}
                    className="flex-1"
                    maxLength={11}
                    inputMode="numeric"
                    autoComplete="off"
                  />
                  <Button type="submit" variant="primary">
                    搜索
                  </Button>
                  {winnersKeywordTrimmed ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setWinnersKeyword('');
                        setWinnersKeywordInput('');
                        setWinnersPage(1);
                      }}
                    >
                      返回
                    </Button>
                  ) : null}
                </form>

                {winnersSearchResultState === 'loading' ? (
                  <div className="flex justify-center py-12">
                    <Spinner />
                  </div>
                ) : winnersSearchResultState === 'not_registered' ? (
                  <Card className="shadow-none">
                    <CardContent>
                      <div className="py-12 text-center text-sm text-default-500">
                        您搜索的手机号没有注册本次活动。
                      </div>
                    </CardContent>
                  </Card>
                ) : winnersSearchResultState === 'not_winner' ? (
                  <Card className="shadow-none">
                    <CardContent>
                      <div className="py-12 text-center text-sm text-default-500">
                        尚未中奖，敬请期待下一轮抽奖
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {winnersLoading ? (
                      <div className="flex justify-center py-12">
                        <Spinner />
                      </div>
                    ) : (
                      <Card className="shadow-none">
                        <CardContent className="p-0">
                          {winnersItems.length === 0 ? (
                            <div className="py-12 text-center text-sm text-default-500">暂无获奖名单</div>
                          ) : (
                            <div className="w-full overflow-x-auto">
                              <table className="w-full border-collapse text-sm">
                                <thead>
                                  <tr className="border-b border-divider">
                                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-default-500">
                                      姓名
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-default-500">
                                      手机号
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-default-500">
                                      奖品名称
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-default-500">
                                      邀请人
                                    </th>
                                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-default-500">
                                      中奖时间
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {winnersItems.map((item: Record<string, unknown>) => {
                                    const inviter = item.inviter as
                                      | { avatarUrl?: string | null; nickname?: string | null; username?: string }
                                      | null
                                      | undefined;
                                    const prize = item.prize as { name?: string } | null | undefined;
                                    const nameDisplay = nameWithGenderDisplay(
                                      item.name as string | undefined | null,
                                      item.gender as string | undefined | null
                                    );
                                    const mobileStr = (item.mobile as string) ?? '—';
                                    const prizeName = prize?.name?.trim() ?? '—';
                                    const inviterDisplay: string = inviter
                                      ? (inviter.nickname && inviter.nickname.trim()) || inviter.username || '—'
                                      : '—';
                                    const wonAt = item.wonAt as string | Date | null | undefined;
                                    const wonAtStr = wonAt ? formatDateTime(wonAt) : '—';
                                    return (
                                      <tr key={String(item.id)} className="border-b border-divider">
                                        <td className="whitespace-nowrap px-3 py-2 text-foreground">{nameDisplay}</td>
                                        <td className="whitespace-nowrap px-3 py-2 tabular-nums text-foreground">
                                          {mobileStr}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-foreground">{prizeName}</td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                          {inviter ? (
                                            <div className="flex min-w-0 items-center gap-2">
                                              <Avatar className="h-7 w-7 shrink-0">
                                                <AvatarImage src={inviter.avatarUrl ?? undefined} alt="" />
                                                <AvatarFallback className="text-xs">
                                                  {inviterDisplay !== '—' ? inviterDisplay.slice(0, 1) : '?'}
                                                </AvatarFallback>
                                              </Avatar>
                                              <span className="truncate text-foreground">{inviterDisplay}</span>
                                            </div>
                                          ) : (
                                            <span className="text-default-500">—</span>
                                          )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-default-500">{wonAtStr}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {(winnersHasPrev || winnersHasNext) && (
                      <div className="flex items-center justify-center gap-4">
                        <button
                          type="button"
                          onClick={() => setWinnersPage((p) => Math.max(1, p - 1))}
                          disabled={!winnersHasPrev}
                          className="rounded-md border border-border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          上一页
                        </button>
                        <span className="text-sm text-default-500">
                          {winnersPage} / {winnersTotalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => setWinnersPage((p) => p + 1)}
                          disabled={!winnersHasNext}
                          className="rounded-md border border-border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          下一页
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            </div>
          </Tabs.Panel>
        </Tabs>

        <footer className="mt-10 space-y-2 px-1 text-center text-xs leading-relaxed text-default-500">
          <p>本活动由安联人寿山东公司主办</p>
          <p>人文派提供技术支持</p>
        </footer>
      </div>

      <Drawer state={registerDrawer}>
        <Drawer.Trigger className="sr-only">
          打开登记
        </Drawer.Trigger>
        <Drawer.Backdrop>
          <Drawer.Content placement="bottom">
            <Drawer.Dialog className="theme-dark-blue bg-background px-0 text-foreground [&_input]:text-foreground [&_input]:caret-foreground">
              <Drawer.Handle />
              <Drawer.Header className="border-b border-border px-4 pb-3 text-center">
                <Drawer.Heading className="text-base font-semibold">登记参与抽奖</Drawer.Heading>
              </Drawer.Header>
              <Drawer.Body className="px-0">
                <form
                  id="fcb-registration-form"
                  className="flex flex-col gap-5 px-4 pb-8 pt-5"
                  onSubmit={handleRegisterSubmit}
                >
                  <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 border-b border-border/50 pb-4">
                    <span className="shrink-0 text-foreground">我预测</span>
                    <span className="min-w-0 text-right text-sm font-medium text-foreground">
                      {matchPredictionButtonLabel(pendingPrediction)}
                    </span>
                  </div>

                  <div className="grid grid-cols-[4.5rem_1fr] items-center gap-x-3 gap-y-1">
                    <Label htmlFor="fcb-reg-name" className="text-foreground">
                      姓名
                    </Label>
                    <Input
                      id="fcb-reg-name"
                      type="text"
                      placeholder="请输入您的姓名"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      autoComplete="name"
                      className="border-border bg-content1/40 text-foreground placeholder:text-default-500"
                    />
                  </div>

                  <div className="grid grid-cols-[4.5rem_1fr] items-start gap-x-3">
                    <Label className="pt-2 text-foreground">尊称</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={regGender === Gender.MALE ? 'primary' : 'outline'}
                        className="flex-1"
                        onClick={() => setRegGender(Gender.MALE)}
                      >
                        {Gender.MALE}
                      </Button>
                      <Button
                        type="button"
                        variant={regGender === Gender.FEMALE ? 'primary' : 'outline'}
                        className="flex-1 text-foreground"
                        onClick={() => setRegGender(Gender.FEMALE)}
                      >
                        {Gender.FEMALE}
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-[4.5rem_1fr] items-center gap-x-3 gap-y-1">
                    <Label htmlFor="fcb-reg-phone" className="text-foreground">
                      手机号
                    </Label>
                    <Input
                      id="fcb-reg-phone"
                      type="tel"
                      placeholder="请输入您的手机号"
                      inputMode="numeric"
                      maxLength={11}
                      autoComplete="off"
                      value={regPhone}
                      onChange={(e) => setRegPhone(e.target.value)}
                      className="border-border bg-content1/40 text-foreground placeholder:text-default-500"
                    />
                  </div>
                </form>
              </Drawer.Body>
              <Drawer.Footer className="border-t border-border px-4 pb-4 pt-3">
                <Button slot="close" variant="secondary">
                  取消
                </Button>
                <Button
                  type="submit"
                  form="fcb-registration-form"
                  variant="primary"
                  isDisabled={isRegistering}
                  className="bg-orange-600 text-white hover:bg-orange-700"
                >
                  {isRegistering ? (
                    <>
                      <Spinner className="mr-1 inline-block size-4" />
                      保存中
                    </>
                  ) : (
                    '保存'
                  )}
                </Button>
              </Drawer.Footer>
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer.Backdrop>
      </Drawer>

      {!registerDrawer.isOpen ? (
        <div className="fixed bottom-0 left-1/2 z-[80] w-full max-w-[var(--app-max-width)] -translate-x-1/2 border-t border-border/60 bg-background/95 backdrop-blur-sm">
          <WechatAction onAction={handleWechatAction} />
        </div>
      ) : null}

      {wechatTipAction ? (
        <WechatActionTips action={wechatTipAction} onClose={() => setWechatTipAction(null)} />
      ) : null}
    </div>
  );
}
